import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ToggleLeft, ToggleRight, Plus, Trash2, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import toast from 'react-hot-toast';
import {
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';

interface FeatureFlag {
  id: string;
  key: string;
  enabled: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

// Well-known feature flags — labels are pulled from i18n settings.knownFlags.*
const KNOWN_FLAG_KEYS = [
  'TEACHING_REPORTS_ENABLED',
  'TRAVEL_EXPENSES_ENABLED',
  'HOSPITAL_MAP_ENABLED',
  'EMAIL_NOTIFICATIONS_ENABLED',
  'PDF_EXPORT_ENABLED',
] as const;

export function FeatureFlagsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const { data: flags, isLoading } = useQuery<FeatureFlag[]>({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      const { data } = await http.get('/settings/features');
      return data.features;
    },
  });

  const toggleFlag = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      const { data } = await http.put(`/settings/features/${key}`, { enabled });
      return data.features;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      toast.success(t('settings.featureUpdated'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const createFlag = useMutation({
    mutationFn: async ({ key, description }: { key: string; description: string }) => {
      const { data } = await http.post('/settings/features', { key, enabled: false, description });
      return data.features;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      setShowAddForm(false);
      setNewKey('');
      setNewDescription('');
      toast.success(t('settings.featureCreated'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const deleteFlag = useMutation({
    mutationFn: async (key: string) => {
      const { data } = await http.delete(`/settings/features/${key}`);
      return data.features;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      toast.success(t('common.deleted'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const getFlagLabel = (flag: FeatureFlag) => {
    if ((KNOWN_FLAG_KEYS as readonly string[]).includes(flag.key)) {
      return t(`settings.knownFlags.${flag.key}`);
    }
    return flag.description || flag.key;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring' as const, stiffness: 350, damping: 30 }}
          className="rounded-xl border border-gray-200 p-8 shadow-sm glass-surface"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </motion.div>
      </div>
    );
  }

  const flagList = flags ?? [];

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* Header with icon badge */}
      <motion.div variants={staggerItem} className="space-y-4">
        <div
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 p-3 shadow-sm glass-surface"
        >
          <ToggleRight className="h-6 w-6 text-slate-800" />
        </div>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">{t('settings.featureFlags')}</h1>
            <p className="mt-1 text-sm text-neutral-600">{t('settings.featureFlagsDescription')}</p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition-all hover:bg-neutral-50 active:translate-y-px active:shadow-sm"
          >
            <Plus className="h-4 w-4" />
            {t('settings.addFlag')}
          </button>
        </div>
      </motion.div>

      {/* Add Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            variants={staggerItem}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -10 }}
            className="rounded-3xl overflow-hidden border border-gray-200 shadow-sm glass-surface"
          >
            <div className="px-6 py-2.5 border-b border-gray-200">
              <h3 className="font-semibold text-xs sm:text-sm">{t('settings.addFlag')}</h3>
            </div>
            <div className="px-6 py-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-neutral-700">
                  {t('settings.flagKey')}
                </label>
                <input
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                  placeholder="FEATURE_NAME_ENABLED"
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 font-mono text-sm font-medium shadow-sm transition-all focus:translate-y-px focus:shadow-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-neutral-700">
                  {t('settings.flagDescription')}
                </label>
                <input
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-sm transition-all focus:translate-y-px focus:shadow-sm focus:outline-none"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={() => newKey && createFlag.mutate({ key: newKey, description: newDescription })}
                  disabled={!newKey || createFlag.isPending}
                  className="rounded-xl border border-gray-200 bg-primary-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-700 active:translate-y-px active:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('common.save')}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewKey('');
                    setNewDescription('');
                  }}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-neutral-600 shadow-sm transition-all hover:bg-neutral-50 active:translate-y-px active:shadow-sm"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Flags list */}
      {flagList.length === 0 ? (
        <motion.div
          variants={staggerItem}
          className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-200 py-16 shadow-sm glass-surface"
        >
          <ToggleLeft className="h-12 w-12 text-neutral-300" />
          <p className="text-sm font-medium text-neutral-500">{t('settings.noFlags')}</p>
        </motion.div>
      ) : (
        <motion.div variants={staggerItem} className="space-y-3">
          {flagList.map((flag, idx) => (
            <motion.div
              key={flag.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                type: 'spring' as const,
                stiffness: 350,
                damping: 30,
                delay: idx * 0.05,
              }}
              className={`flex items-center gap-4 rounded-xl border border-gray-200 px-5 py-4 shadow-sm transition-all hover:bg-neutral-50 glass-surface ${flag.enabled ? 'bg-white' : 'bg-neutral-50'}`}
            >
              <button
                onClick={() => toggleFlag.mutate({ key: flag.key, enabled: !flag.enabled })}
                className="shrink-0 transition-transform hover:scale-110 active:scale-95"
              >
                {flag.enabled ? (
                  <ToggleRight className="h-8 w-8 text-success-500 drop-shadow-sm" />
                ) : (
                  <ToggleLeft className="h-8 w-8 text-neutral-400" />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-bold text-neutral-900">{getFlagLabel(flag)}</span>
                  <span
                    style={{
                      backgroundColor: flag.enabled ? '#ecfdf5' : '#f3f4f6',
                      color: flag.enabled ? '#047857' : '#6b7280',
                    }}
                    className="rounded-full border border-gray-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm"
                  >
                    {flag.enabled ? t('settings.enabled') : t('settings.disabled')}
                  </span>
                </div>
                <p className="mt-0.5 font-mono text-xs text-neutral-500">{flag.key}</p>
              </div>
              <button
                onClick={() => {
                  if (confirm(t('settings.confirmDeleteFlag'))) {
                    deleteFlag.mutate(flag.key);
                  }
                }}
                className="shrink-0 rounded-lg border border-transparent p-2 text-neutral-400 transition-all hover:border-red-300 hover:bg-red-50 hover:text-red-500 active:scale-95"
                title={t('common.delete')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}
